<template>
  <div class="autosuggest-container">
    <div
      :class="[
        'autosuggest-container-ricerca',
        'dea-ico-ricerca',
        { 'autosuggest-container-ricerca--home-page': isPageHome },
        autosuggest.containerClassModifier,
      ]"
      @click="openSearch()"
    >
      <input
        :id="`${component}-search-input`"
        v-model="inputQuery"
        class="autosuggest-search-input"
        :class="[
          searchClass,
          inputModifierClass,
          { 'search-open': component === 'header' ? searchOpen : true },
          `${component === 'header' ? '' : 'portal-search-input-font'}`,
        ]"
        :placeholder="autosuggestPlaceholder"
        autocomplete="off"
        type="text"
        data-test="autosuggest-input"
        @keyup.enter="goToSearchPage()"
      />
      <input v-if="filterByGrade" id="hiddenGrade" name="grade" type="hidden" :value="targetGrade" />
      <div v-if="searchOpen && inputQuery.length" class="autosuggest--delete">
        <div class="icon dea-ico-close" @mousedown="resetInput"></div>
      </div>
    </div>
    <div
      class="autosuggest-container-results"
      :class="{ 'autosuggest-container-results--guided-search-active': showGuidedSearch }"
    >
      <slot v-if="showGuidedSearch" name="guidedSearchHeader"></slot>
      <slot v-if="showGuidedSearch" name="guidedSearchHero"></slot>
      <component
        :is="resultsListComponent"
        v-if="searchOpen && showList"
        :config="autosuggest"
        :data="filteredResults"
        :group-color="$root.config.cssClasses.colorMod"
        :group-names="autosuggest.groupNames"
      ></component>
    </div>
    <div
      v-if="openOverlay && useOverlay"
      :class="[
        'autosuggest__overlay',
        { 'autosuggest__overlay--scrolled': !showHeaderTop },
        { 'autosuggest__overlay--search': !showHeaderBottom },
      ]"
    ></div>
  </div>
</template>

<script>
import autosuggestResults from "@/components/mixins/autosuggestResults";
import AutosuggestListResults from "@/components/elements/AutosuggestListResults"; // eslint-disable-next-line vue/no-unused-components
import handleWindowResize from "@/components/mixins/handleWindowResize";
import headerBase from "@/components/mixins/headerBase";
import variables from "@/styles/variables.scss";

export default {
  name: "Autosuggest",

  mixins: [autosuggestResults, handleWindowResize, headerBase, AutosuggestListResults],
  props: {
    /**
     * Autosuggest configuration object
     */
    autosuggest: {
      type: Object,
      required: true,
      default: () => {},
    },
    /**
     * Component placing
     */
    component: {
      type: String,
      default: "header",
    },
    /**
     * Main configuration object (usually $root.config)
     */
    config: {
      type: Object,
      default: () => {},
    },
    /**
     * Dynamic prop to inform if the current URL is the site home page
     */
    isPageHome: {
      type: Boolean,
      default: false,
    },
    /**
     * Dynamic prop to inform if HeaderBottom is currently visible.
     *
     * Set to false if using component outside the header
     *
     */
    showHeaderBottom: {
      type: Boolean,
      default: false,
    },
    /**
     * Dynamic prop to inform if HeaderTop is currently visible.
     *
     * Set to false if using component outside the header
     *
     */
    showHeaderTop: {
      type: Boolean,
      default: true,
    },
    /**
     * Activate overlay when autosuggest is open.
     */
    useOverlay: {
      type: Boolean,
      default: true,
    },
    /**
     * Dictionary of the uiser target hirearchy structure, this is usually generated by the backend
     */
    targetInfo: {
      type: Object,
      default: () => {},
    },
    /**
     * Component used to render the result string. If null, AutosuggestListResults is used
     */
    resultsComponent: {
      type: Object,
      default: null,
    },
    /**
     * If autosuggest is dynamic set to false to not pass `grade` querystring parameter
     */
    filterByGrade: {
      type: Boolean,
      default: true,
    },
    /**
     * CSS class applied to the input field, use to customize its appearance
     */
    inputModifierClass: {
      type: String,
      default: "",
    },
    /**
     * Placeholder for input
     */
    placeholder: {
      type: String,
      default: "Cosa stai cercando?",
    },
  },
  data() {
    return {
      // use the component passed as prop if set, otherwise it defaults to AutosuggestListResults
      resultsListComponent: this.resultsComponent ? this.resultsComponent : AutosuggestListResults,
      inputQuery: "",
      openClass: false,
      openOverlay: false,
      resetInputQuery: false,
      searchOpen: false,
      guidedSearchOpen: false,
    };
  },
  computed: {
    autosuggestPlaceholder() {
      return this.autosuggest.placeholder ? this.autosuggest.placeholder : this.placeholder;
    },
    showGuidedSearch() {
      if ((!this.searchOpen || !this.showList) && this.guidedSearchOpen) return true;
      return false;
    },
    showList() {
      if (!this.autosuggest.isStaticSearch && this.inputQuery.length < this.minCharSearch) {
        return false;
      }
      return Object.keys(this.filteredResults).some(key => this.filteredResults[key].length !== 0);
    },
    searchClass() {
      if (this.autosuggest.initialStatus === "closed" || this.windowWidth < variables.bpSizeLarge) {
        return "search-closed";
      }
      return "search-compact";
    },
    targetGrade() {
      return this.targetInfo && this.targetInfo.target_grade ? this.targetInfo.target_grade : "";
    },
  },
  mounted() {
    window.addEventListener("click", this.clickListener);
  },
  destroyed() {
    window.removeEventListener("click", this.clickListener);
  },
  methods: {
    closeSearch() {
      if (this.resetInputQuery) {
        this.inputQuery = "";
        this.resetInputQuery = false;
        return;
      }
      this.searchOpen = false;
      this.guidedSearchOpen = false;
      this.openOverlay = false;
      this.$emit("close-search");
    },
    goToSearchPage() {
      const hiddenGradeElem = document.getElementById("hiddenGrade");
      let gradeParam = "";
      if (hiddenGradeElem) {
        gradeParam = hiddenGradeElem.value ? `&grade=${hiddenGradeElem.value}` : "";
      }
      window.open(`${this.autosuggest.searchUrl}?q=${this.inputQuery}${gradeParam}`, "_self");
    },
    openSearch() {
      this.searchOpen = true;
      this.guidedSearchOpen = true;
      const searchInput = document.getElementById(`${this.component}-search-input`);
      searchInput.focus();
      this.openOverlay = true;
      this.$emit("open-search");
    },
    resetInput() {
      this.resetInputQuery = true;
    },
    clickListener(event) {
      const searchInput = document.getElementById(`${this.component}-search-input`);
      const searchContainer = document.getElementsByClassName("autosuggest-container-ricerca")[0];
      if (event.target !== searchInput && event.target !== searchContainer) {
        this.closeSearch();
      }
    },
  },
};
</script>

<style lang="scss">
// sass-lint:disable no-ids, id-name-format
@import "@/styles/system.utils.scss"; //sass-lint:disable-line clean-import-paths

$search-compact-width: 240px;
$autocomplete-input-hero-height: 56px;
$guided-search-radius: 16px;

@mixin in-page() {
  .autosuggest-container-ricerca {
    padding: $space-small 0 $space-small $space-x-small;

    &::before {
      transform: translate(-$space-x-small, -$space-xx-small);
    }

    .autosuggest--delete .icon {
      transform: translateY(-$space-xx-small);
    }
  }

  .autosuggest-container-results {
    top: $autocomplete-input-hero-height - $space-small - 1px;
  }

  .autosuggest-results-list {
    border: $border-light $dea-color-black;
  }

  .autosuggest-search-input {
    padding-left: 0;
  }
}

.autosuggest-container {
  @include media("<s") {
    z-index: 2;
  }

  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  width: 100%;

  &--in-page {
    @include in-page();
  }

  &-ricerca {
    @include media("<s") {
      background-color: transparent;
      padding: $space-small 0;
    }

    &--home-page {
      background-color: transparent;
    }

    background-color: $dea-color-white;
    display: flex;
    height: 100%;
    justify-content: flex-end;
    padding: $space-small;
    position: relative;
    width: 100%;
    z-index: 1;

    &--in-page {
      background: transparent;
    }

    &::before {
      @include media("<=xs") {
        font-size: $space-small;
        transform: translate($space-small, $space-xm-small);
      }

      font-size: $space-base;
      transform: translate($space-base, $space-x-small);
    }

    .autosuggest--delete {
      align-items: center;
      cursor: pointer;
      display: flex;
      height: 100%;
      position: absolute;
      right: $space-base;
      top: 0;

      & .icon {
        align-items: center;
        background: $dea-color-grey-light;
        border: $border-light $dea-color-grey-light;
        border-radius: $border-radius-circle;
        display: flex;
        height: $space-base;
        justify-content: center;
        width: $space-base;
      }

      .dea-ico-close::before {
        position: relative !important; //sass-lint:disable-line no-important
        vertical-align: middle;
      }
    }
  }
}

.autosuggest-wrapper--container-overlay {
  .autosuggest-container-ricerca {
    &::before {
      @include media("<=xs") {
        font-size: $space-small;
        transform: translate($space-small, $space-x-small);
      }

      font-size: $space-base;
      transform: translateX($space-base);
    }
  }
}

.autosuggest-search-input {
  @include media("<=xs") {
    padding-left: $space-base;
  }

  background-color: transparent;
  border: 0;
  border-bottom: $border-light $dea-color-black;
  outline: 0;
  padding-left: $space-intermediate;
  width: 100%;

  &::placeholder {
    color: $dea-color-grey-dark;
  }
}

.portal-search-input-font {
  @include h-font-size-small;
}

.search-closed {
  border: 0;
  overflow: hidden;
  width: 0;
}

.search-compact {
  max-width: $search-compact-width;
  width: 100%;

  &.search-open {
    max-width: unset;
  }
}

.search-open {
  border-bottom: $border-light $dea-color-black;
  margin-right: $space-xm-small;
  width: 100%;
}

.autosuggest-container-results {
  background-color: $dea-color-white;
  display: flex;
  flex-flow: column;
  position: absolute;
  top: $dea-header-height;
  width: 100%;
  z-index: 1;

  &--guided-search-active {
    border-bottom-left-radius: $guided-search-radius;
    border-bottom-right-radius: $guided-search-radius;
  }
}

.autosuggest__overlay {
  background: $dea-color-black;
  height: 100%;
  left: 0;
  opacity: $opacity-medium;
  position: fixed;
  top: $dea-header-top-height + $dea-header-height + $dea-header-bottom-height;
  width: 100%;
  z-index: -1;

  &--scrolled {
    @include media("<=m") {
      top: $dea-header-height + $dea-header-bottom-mobile-height;
    }
    top: $dea-header-height + $dea-header-bottom-height;
  }

  &.autosuggest__overlay--search {
    top: $dea-header-top-height + $dea-header-height;

    &.autosuggest__overlay--scrolled {
      @include media("<=m") {
        top: $dea-header-height;
      }
      top: $dea-header-height;
    }
  }
}

#autosuggest-app {
  .autosuggest-container-results {
    top: $autocomplete-input-hero-height;
  }
}

/* style variants used when autosuggest is outside the header */
.dea-autosuggest--in-page {
  @include in-page();
}
</style>
